const BASE = '/api';

function getToken() { return localStorage.getItem('sp_token') || ''; }

async function apiFetch(path, opts = {}) {
  // Timeout di 15 secondi
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(BASE + path, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
        ...opts.headers
      },
      ...opts,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (res.status === 401) {
      localStorage.removeItem('sp_token');
      localStorage.removeItem('sp_user');
      window.location.reload();
      throw new Error('Sessione scaduta');
    }

    // Se il server risponde con HTML invece di JSON (es. nginx error page)
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error('Server non raggiungibile — riprova tra qualche secondo');
    }

    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Errore sconosciuto');
    return json.data;

  } catch(e) {
    clearTimeout(timeout);
    if (e.name === 'AbortError') {
      throw new Error('Timeout — il server impiega troppo a rispondere');
    }
    throw e;
  }
}

export function saveAuth(token, user) {
  localStorage.setItem('sp_token', token);
  localStorage.setItem('sp_user', JSON.stringify(user));
}

export function getUser() {
  try { return JSON.parse(localStorage.getItem('sp_user')); } catch(e) { return null; }
}

export function logout() {
  localStorage.removeItem('sp_token');
  localStorage.removeItem('sp_user');
}

export const api = {
  // Auth
  login: (data) => apiFetch('/auth/login', { method:'POST', body:data }),
  me:    ()     => apiFetch('/auth/me'),
  changePassword: (data) => apiFetch('/auth/password', { method:'POST', body:data }),
  // Utenti
  getUtenti:    ()       => apiFetch('/utenti'),
  createUtente: (data)   => apiFetch('/utenti', { method:'POST', body:data }),
  updateUtente: (id,data)=> apiFetch(`/utenti/${id}`, { method:'PUT', body:data }),
  deleteUtente: (id)     => apiFetch(`/utenti/${id}`, { method:'DELETE' }),
  // Dashboard
  dashboard: () => apiFetch('/dashboard'),

  // Clienti
  getClienti:  (params = {}) => apiFetch('/clienti?' + new URLSearchParams(params)),
  getCliente:  (id)          => apiFetch(`/clienti/${id}`),
  createCliente:(data)       => apiFetch('/clienti', { method:'POST', body:data }),
  updateCliente:(id, data)   => apiFetch(`/clienti/${id}`, { method:'PUT', body:data }),
  deleteCliente:(id)         => apiFetch(`/clienti/${id}`, { method:'DELETE' }),

  // Scadenze
  getScadenze:  (params = {}) => apiFetch('/scadenze?' + new URLSearchParams(params)),
  getScadenza:  (id)           => apiFetch(`/scadenze/${id}`),
  createScadenza:(data)        => apiFetch('/scadenze', { method:'POST', body:data }),
  updateScadenza:(id, data)    => apiFetch(`/scadenze/${id}`, { method:'PUT', body:data }),
  patchStato:   (id, stato)    => apiFetch(`/scadenze/${id}/stato`, { method:'PATCH', body:{stato} }),
  deleteScadenza:(id)          => apiFetch(`/scadenze/${id}`, { method:'DELETE' }),

  // Banche
  getBanche:    ()       => apiFetch('/banche'),
  createBanca:  (data)   => apiFetch('/banche', { method:'POST', body:data }),
  deleteBanca:  (id)     => apiFetch(`/banche/${id}`, { method:'DELETE' }),

  // Attività
  getAttivita:  (params = {}) => apiFetch('/attivita?' + new URLSearchParams(params)),

  // Cedenti
  getCedenti:   ()       => apiFetch('/cedenti'),
  createCedente:(data)   => apiFetch('/cedenti', { method:'POST', body:data }),

  // Cessione quote
  getCessquote: ()       => apiFetch('/cessquote'),

  // Scadenze raggruppate per titolo
  getScadenzeRaggruppate: (params = {}) => apiFetch('/scadenze/raggruppate?' + new URLSearchParams(params)),

  // Avvisi e Cartelle
  getAvvisi:       (params={}) => apiFetch('/avvisi?' + new URLSearchParams(params)),
  getAvviso:       (id)        => apiFetch(`/avvisi/${id}`),
  createAvviso:    (data)      => apiFetch('/avvisi', { method:'POST', body:data }),
  updateAvviso:    (id, data)  => apiFetch(`/avvisi/${id}`, { method:'PUT', body:data }),
  deleteAvviso:    (id)        => apiFetch(`/avvisi/${id}`, { method:'DELETE' }),
  saveRate:        (id, rate)  => apiFetch(`/avvisi/${id}/rate`, { method:'POST', body:{rate} }),
  patchRataStato:  (id, stato) => apiFetch(`/avvisi_rate/${id}/stato`, { method:'PATCH', body:{stato} }),
  uploadAllegatoAvviso: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`/api/avvisi/${id}/allegati`, { method:'POST', body:fd, headers:{Authorization:`Bearer ${localStorage.getItem('sp_token')||''}`} })
      .then(r=>r.json()).then(j=>{ if(!j.success) throw new Error(j.error); return j.data; });
  },
  deleteAllegatoAvviso: (id, filename) => apiFetch(`/avvisi/${id}/allegati/${filename}`, { method:'DELETE' }),
  importRateAvviso: (id, file) => {
    const fd = new FormData(); fd.append('pdf', file);
    return fetch(`/api/avvisi/${id}/import-rate`, { method:'POST', body:fd })
      .then(r=>r.json()).then(j=>{ if(!j.success) throw new Error(j.error); return j.data; });
  },

  // Import piano rateazione PDF
  parseRateazione: (file) => {
    const fd = new FormData();
    fd.append('pdf', file);
    return fetch('/api/import/rateazione', { method:'POST', body:fd })
      .then(r => r.json())
      .then(j => { if (!j.success) throw new Error(j.error); return j.data; });
  },
  confermaRateazione: (data) => apiFetch('/import/rateazione/conferma', { method:'POST', body:data }),

  // Allegati cliente
  getAllegati:    (id)         => apiFetch(`/clienti/${id}/allegati`),
  uploadAllegato:(id, file)   => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`/api/clienti/${id}/allegati`, { method: 'POST', body: fd })
      .then(r => r.json())
      .then(j => { if (!j.success) throw new Error(j.error); return j.data; });
  },
  deleteAllegato:(id, filename) => apiFetch(`/clienti/${id}/allegati/${filename}`, { method: 'DELETE' }),

  // Metodi generici
  get:    (path)        => apiFetch(path.replace('/api','')),
  post:   (path, data)  => apiFetch(path.replace('/api',''), { method:'POST', body:data }),
  put:    (path, data)  => apiFetch(path.replace('/api',''), { method:'PUT', body:data }),
  delete: (path)        => apiFetch(path.replace('/api',''), { method:'DELETE' }),

  // Progetti
  getProgetti:      (params={}) => apiFetch('/progetti?' + new URLSearchParams(params)),
  getProgetto:      (id)        => apiFetch(`/progetti/${id}`),
  createProgetto:   (data)      => apiFetch('/progetti', { method:'POST', body:data }),
  updateProgetto:   (id, data)  => apiFetch(`/progetti/${id}`, { method:'PUT', body:data }),
  deleteProgetto:   (id)        => apiFetch(`/progetti/${id}`, { method:'DELETE' }),
  getFasiProgetto:  (id)        => apiFetch(`/progetti/${id}/fasi`),
  createFase:       (id, data)  => apiFetch(`/progetti/${id}/fasi`, { method:'POST', body:data }),
  updateFase:       (id, data)  => apiFetch(`/fasi/${id}`, { method:'PUT', body:data }),
  deleteFase:       (id)        => apiFetch(`/fasi/${id}`, { method:'DELETE' }),
  riordinaFasi:     (id, ordine)=> apiFetch(`/progetti/${id}/fasi/riordina`, { method:'PUT', body:{ordine} }),
  createAttivita:   (idFase, data) => apiFetch(`/fasi/${idFase}/attivita`, { method:'POST', body:data }),
  updateAttivita:   (id, data)  => apiFetch(`/attivita-fase/${id}`, { method:'PUT', body:data }),
  deleteAttivita:   (id)        => apiFetch(`/attivita-fase/${id}`, { method:'DELETE' }),
};
